import { useKeycloak } from '@react-keycloak/web'
import React, { createContext, Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from 'react'
import { matchPath, useLocation, useNavigate } from 'react-router-dom'
import {
  ADMIN_MAIN_MENU_LINK,
  APP_LOGO,
  BASE_BRANDING_LOGO,
  BRANDING_LOGOS,
  CouncilBrandingLogo,
  MAIN_MENU_LINKS,
  UNAUTHENTICATED_MAIN_MENU_LINKS,
} from '../../../constants'
import { Permissions } from '../../../types/user-types'
import { useGetBannerData } from '../../hooks/use-api'
import useLogout from '../../hooks/use-logout'
import useUser from '../../hooks/use-user'
import useWellExport from '../../hooks/use-well-export'
import { authButtonHandlerFactory, isValidToken } from '../../services/auth-utils'
import { getQuickSearchDisplayOptions } from '../../services/form-utils'
import { can, canCreateWell, canViewWell, isAdminUser, isCouncilAdmin } from '../../services/user-utils'
import AppShellErrorBoundary from '../errors/components/error-boundary'
import RegisterModal from '../register-modal'
import SignInModal from '../sign-in-modal'
import { determineMessageType, isBannerDataValid, onBannerClose, shouldShowBanner } from '../wells/banner'
import { Banner, Button, ButtonsContainer, MenuActionProps, MenuLinkProps, NavPanel } from '@msaf/core-react'
import AppShell from '../aoraki/navigation/app-shell'

export interface PageProps {
  isSearchMenu?: boolean
  isDownloadsMenu?: boolean
  isNotificationsMenu?: boolean
}

interface ActionMenuInterface {
  setIsAGSUploadModalOpen: Dispatch<SetStateAction<boolean>>
  setIsDeleteWellModalOpen: Dispatch<SetStateAction<boolean>>
  isAGSUploadModalOpen: boolean
  isDeleteWellModalOpen: boolean
  setShouldShowAGSLink: Dispatch<SetStateAction<boolean>>
  showStrataExport: boolean
  setShowStrataExport: Dispatch<SetStateAction<boolean>>
}

interface ActionsMenuItems {
  label: string
  action: () => void
}

export const ActionMenuContext = createContext<ActionMenuInterface>(null!)

export default function Page({ children, isDownloadsMenu, isNotificationsMenu }: React.PropsWithChildren<PageProps>) {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { logout } = useLogout()
  const [isMenuHidden, setIsMenuHidden] = useState(false)
  const [isSignInModalOpen, setIsSignInModalOpen] = useState(false)
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false)
  const [isAGSUploadModalOpen, setIsAGSUploadModalOpen] = useState(false)
  const [isDeleteWellModalOpen, setIsDeleteWellModalOpen] = useState(false)
  const [shouldShowAGSLink, setShouldShowAGSLink] = useState(false)
  const [showStrataExport, setShowStrataExport] = useState(false)
  const [brandingLogos, setBrandingLogos] = useState<Array<CouncilBrandingLogo>>([BASE_BRANDING_LOGO])
  const { wellExport } = useWellExport()
  const [actionsMenuItems, setActionsMenuItems] = useState<Array<ActionsMenuItems>>()
  const { data: bannerData } = useGetBannerData()
  // Used for toggling side menu on mobile from within Header
  const pageEl = useRef<HTMLDivElement>(null)
  const { user, isLoading } = useUser()
  const { keycloak } = useKeycloak()

  const mainMenuLinks = useMemo(() => {
    if (isLoading) return
    if (!isValidToken(user?.token)) return UNAUTHENTICATED_MAIN_MENU_LINKS
    return [...MAIN_MENU_LINKS, ...(isAdminUser(user) ? [ADMIN_MAIN_MENU_LINK] : [])]
  }, [user, isLoading])

  const userMenuLinks = useMemo(() => {
    const links: Array<MenuActionProps | MenuLinkProps> = []
    if (isValidToken(user?.token)) {
      links.push({
        label: 'Change password',
        icon: 'settings',
        action: () => keycloak.login({ action: 'UPDATE_PASSWORD' }),
      })

      if (can(user, [Permissions.MAINTAIN_API_CLIENTS])) {
        links.push({
          label: 'Manage API clients',
          icon: 'share',
          action: () => navigate('/user/api-clients'),
        })
      }

      if (isValidToken(user?.token)) {
        links.push({
          path: '',
          label: 'Logout',
          icon: 'exit',
          action: () => logout(),
        })
      }
    }
    return links
  }, [keycloak, logout, navigate, user])

  const wellExportItem = {
    label: 'Request PDF export of well',
    action: () => wellExport(),
  }

  const deleteWellItem = {
    label: 'Delete well record',
    action: () => setIsDeleteWellModalOpen(true),
  }

  const agsUploadItem = {
    label: 'Upload AGS File',
    action: () => setIsAGSUploadModalOpen(true),
  }

  const strataExportItem = {
    label: 'Export well strata',
    action: () => setShowStrataExport(true),
  }

  useEffect(() => {
    // Don't show actions menu on Setup page
    if (isSetupRoute()) {
      return setActionsMenuItems(undefined)
    }

    const menuItems = []
    const isWellExport = isWellExportRoute()
    const isDeleteWell = isDeleteWellRoute()
    const isASGUpload = shouldShowAGSLink && isAGSUploadRoute()
    const isStrataExport = isWellExportRoute() // Use same route matching as PDF export

    if (isDeleteWell) {
      menuItems.push(deleteWellItem)
    }

    if (isASGUpload) {
      menuItems.push(agsUploadItem)
    }

    if (isWellExport) {
      menuItems.push(wellExportItem)
    }

    if (isStrataExport) {
      menuItems.push(strataExportItem)
    }

    const menuItemsToShow = menuItems.length ? menuItems : undefined
    setActionsMenuItems(menuItemsToShow)

    // Eslint suggests multiple additions to dependency array, we really only want this effect to run when browser url changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, pathname, shouldShowAGSLink])

  const isAGSUploadRoute = () => {
    return (
      can(user, [Permissions.CREATE_WELL, Permissions.CREATE_WELL_UNSCOPED]) &&
      matchPath({ path: '/wells/:id/edit', end: false }, pathname)
    )
  }

  const isSetupRoute = () => {
    return matchPath('/wells/:id/edit/setup', pathname)
  }

  const isDeleteWellRoute = () => {
    return (
      canCreateWell(user) &&
      (isAdminUser(user) || isCouncilAdmin(user)) &&
      (matchPath({ path: '/wells/:id/view', end: false }, pathname) ||
        matchPath({ path: '/wells/:id/edit', end: false }, pathname))
    )
  }

  const isWellExportRoute = () => {
    // Is an export route if url includes wells and well id is defined
    return (
      canViewWell(user) &&
      (matchPath({ path: '/wells/:id/view', end: false }, pathname) ||
        matchPath({ path: '/wells/:id/edit', end: false }, pathname))
    )
  }

  const openMenu = () => {
    pageEl?.current?.classList.add('l-page--menu-open')
  }

  // Show/hide menu panel (mobile only via CSS breakpoints):
  const closeMenu = () => {
    pageEl?.current?.classList.remove('l-page--menu-open')
  }

  const unauthenticatedUserComponent = (
    <ButtonsContainer containerStyle='right'>
      <Button
        buttonStyle='secondary'
        label='Sign in'
        onClick={authButtonHandlerFactory(pathname, setIsSignInModalOpen)}
      />
      <Button label='Register' onClick={authButtonHandlerFactory(pathname, setIsRegisterModalOpen)} />
    </ButtonsContainer>
  )

  useEffect(() => {
    if (user?.councilId) {
      const councilLogo = BRANDING_LOGOS.filter((logo) => logo.councilId === user.councilId)
      setBrandingLogos([...councilLogo, BASE_BRANDING_LOGO])
    }
    // Disable due to suggestion of brandingLogos which causes re-render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  return (
    <>
      {bannerData && isBannerDataValid(bannerData) && (
        <Banner
          message={bannerData.message}
          messageType={determineMessageType(bannerData?.messageType)}
          shouldDisplay={shouldShowBanner(bannerData)}
          onClose={() => onBannerClose(bannerData.expiry)}
        />
      )}
      <div className='l-page' ref={pageEl}>
        <SignInModal isOpen={isSignInModalOpen} closeModal={() => setIsSignInModalOpen(false)} />
        <RegisterModal isOpen={isRegisterModalOpen} closeModal={() => setIsRegisterModalOpen(false)} />
        <NavPanel
          appLogo={APP_LOGO}
          brandingLogos={brandingLogos}
          mainMenuLinks={
            isLoading
              ? undefined // TODO: Add skeleton state if possible
              : mainMenuLinks
          }
          isMenuHidden={isMenuHidden}
          closeMenu={closeMenu}
        />
        <ActionMenuContext.Provider
          value={{
            isAGSUploadModalOpen,
            setIsAGSUploadModalOpen,
            isDeleteWellModalOpen,
            setIsDeleteWellModalOpen,
            setShouldShowAGSLink,
            showStrataExport,
            setShowStrataExport,
          }}
        >
          <AppShellErrorBoundary>
            <AppShell
              hideMenu={(isHidden: boolean) => setIsMenuHidden(isHidden)}
              openMenu={openMenu}
              user={{
                initials: user?.firstName && user.lastName ? user.firstName[0] + user.lastName[0] : '',
                firstName: user?.firstName || '',
                lastName: user?.lastName || '',
                email: user?.email || '',
                hasValidToken: isValidToken(user?.token),
              }}
              actionsMenuItems={actionsMenuItems}
              userMenuLinks={userMenuLinks}
              isDownloadsMenu={!!isDownloadsMenu}
              isNotificationsMenu={!!isNotificationsMenu}
              unauthenticatedUserComponent={isValidToken(user?.token) ? <></> : unauthenticatedUserComponent}
              quickSearchConfig={{
                searchTypeKey: 'search-wells-national',
                onRowClick: (id: string) => navigate(`/wells/${id}/view/summary-details`),
                placeholderText: 'Well number or well name',
                getDisplayOptions: getQuickSearchDisplayOptions,
              }}
            >
              {children}
            </AppShell>
          </AppShellErrorBoundary>
        </ActionMenuContext.Provider>
      </div>
    </>
  )
}
